import cx from 'clsx';
import React, { useEffect, useState } from 'react';

import { ReactComponent as WarningIcon } from '@assets/svg/ic-warning.svg';

import Accordion from '@components/common/Accordion/Accordion';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import type { IActionItem, INetworkTextBlocks } from '@model/actionItem';

import { selectAITranslateNetworkAiTextBlocks } from '@pages/ActionItem/store/useAITranslateStore';

import { selectAppPersistOnBoardStart } from '@store/useAppStorePersist';
import { useModalActions } from '@store/useModalStore';

import useLiveTranslateNetworkTextBlocks from '../../hooks/useLiveTranslateNetworkTextBlocks';
import TranslateBtn from '../TranslateBtn/TranslateBtn';
import AiExtraBlock from './AiExtraBlock';
import style from './style.module.scss';

interface IProps {
  actionItem: IActionItem;
  procedureInfo: INetworkTextBlocks;
}

const NetworkAiExtraBlocks: React.FC<IProps> = ({ actionItem, procedureInfo }) => {
  const { t } = useTranslation();

  const { setActiveModal } = useModalActions();
  const [isTranslated, setIsTranslated] = useState(false);

  const translates = selectAITranslateNetworkAiTextBlocks();

  const { hasTranslate, isTranslateLoading, toggleIsTranslate } = useLiveTranslateNetworkTextBlocks({
    item: actionItem,
    fields: ['description'],
    entityId: actionItem?.id,
    translationFields: [
      'description',
      'battery_required',
      'replacement_part_required',
      'procedure',
      'troubleshooting_description',
      'completion_requirements_description',
      'safety_warnings_description',
    ],
  });

  const {
    battery_required,
    replacement_part_required,
    procedure,
    troubleshooting_description,
    completion_requirements_description,
    safety_warnings_description,
  } = procedureInfo as INetworkTextBlocks;

  const onBoard = selectAppPersistOnBoardStart();

  const handleOpenLightbox = (event: React.MouseEvent<HTMLElement>, key: string): void => {
    const target = event.target;

    if (!(target instanceof HTMLImageElement) || !procedureInfo) return;

    const images = procedureInfo?.[key as keyof typeof procedureInfo]
      .match(/<img [^>]*src="[^"]*"[^>]*>/gm)!
      .map((x) => {
        return { src: x.replace(/.*src="([^"]*)".*/, '$1') };
      });

    if (images.length) {
      const index = images.findIndex((image) => image.src === target?.currentSrc);

      setActiveModal({ name: MODAL_NAMES.lightboxModal, options: { images, index } });
    }
  };

  const handleToggleIsTranslate = () => {
    toggleIsTranslate();
    setIsTranslated(!isTranslated);
  };

  return (
    <>
      {battery_required && (
        <AiExtraBlock
          title={t('Battery Required')}
          originalText={battery_required}
          translatedText={translates['battery_required']}
          hasTranslate={hasTranslate}
          isTranslateLoading={isTranslateLoading}
          toggleIsTranslate={toggleIsTranslate}
          handleOpenLightbox={(event) => handleOpenLightbox(event, 'battery_required')}
        />
      )}
      {replacement_part_required && (
        <AiExtraBlock
          title={t('Replacement Part Required')}
          originalText={replacement_part_required}
          translatedText={translates['replacement_part_required']}
          hasTranslate={hasTranslate}
          isTranslateLoading={isTranslateLoading}
          toggleIsTranslate={toggleIsTranslate}
          handleOpenLightbox={(event) => handleOpenLightbox(event, 'replacement_part_required')}
        />
      )}
      <div className={style.block}>
        <div className={style.blockHead}>
          <div className={style.blockTitle} id="tourStep-14">
            {t('Procedure')}
          </div>
          <TranslateBtn
            isTranslated={isTranslated}
            hasTranslate={hasTranslate}
            isTranslateLoading={isTranslateLoading}
            toggleIsTranslate={handleToggleIsTranslate}
          />
        </div>
        <div className={cx(style.blockContainer, { [style.onBoardBlockHide]: onBoard })}>
          <div
            className={style.infoText}
            dangerouslySetInnerHTML={{
              __html: isTranslated ? translates['procedure'] : procedure,
            }}
            onClick={(event) => handleOpenLightbox(event, 'procedure')}
          />
          <Accordion title={t('Troubleshooting')} variant="bordered">
            <div
              className={cx(style.accordContent, style.infoText)}
              dangerouslySetInnerHTML={{
                __html: isTranslated
                  ? translates['troubleshooting_description']
                  : troubleshooting_description,
              }}
            />
          </Accordion>
          <Accordion title={t('Completion requirements')} variant="bordered">
            <div
              className={cx(style.accordContent, style.infoText)}
              dangerouslySetInnerHTML={{
                __html: isTranslated
                  ? translates['completion_requirements_description']
                  : completion_requirements_description,
              }}
            />
          </Accordion>
          <Accordion
            title={
              <div className={style.accordTitle}>
                <WarningIcon className={style.warningIcon} />
                {t('Safety warnings')}
              </div>
            }
            variant="bordered"
          >
            <div
              className={cx(style.accordContent, style.infoText)}
              dangerouslySetInnerHTML={{
                __html: isTranslated
                  ? translates['safety_warnings_description']
                  : safety_warnings_description,
              }}
            />
          </Accordion>
        </div>
      </div>
    </>
  );
};

export default NetworkAiExtraBlocks;
